/* eslint-disable no-param-reassign */
import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { createTransform } from 'redux-persist';
import { REQUEST_TYPE_VISITOR } from 'core/utils/value';
import { dashboardVisitRequest, dashboardFilterRequest, exportCompletedVisits } from './dashboardApi';
import { DashboardState, DataProps, RequestDataResponse } from './types';

// Transient loading placeholder (ref 0 renders blank); the request type just
// needs a valid value to satisfy the RequestType union — it's never shown as
// real data, being replaced on first fetch.
const emptyRequestState: RequestDataResponse = {
    encryptedId: '',
    ref: 0,
    type: REQUEST_TYPE_VISITOR,
    site: {
        name: '',
        address: '',
        id: 0,
    },
    requestedPeriod: {
        earliest: {
            date: '',
            startTime: '',
            endTime: '',
        },
        latest: {
            date: '',
            startTime: '',
            endTime: '',
        },
    },
    requester: {
        name: '',
        email: '',
        role: {},
    },
    status: {},
    dateSubmitted: '',
    approver: {
        name: '',
        email: '',
        role: {},
    },
    vodafoneMitieApprover: {
        name: '',
        email: '',
        role: {},
    },
    currentStep: 0,
    onBehalfEmail: null,
};

const initialState = {
    error: null,
    isFetching: false,
    isExporting: false,
    activeExportCount: 0,
    requestData: [emptyRequestState],
    pagination: {
        currentPage: 1,
        pageSize: 20,
        total: 0,
    },
    summaryData: {
        approved: 0,
        completed: 0,
        pending: 0,
        rejectedQueried: 0,
    },
    filterData: {
        statuses: {},
        sites: [],
    },
} as DashboardState;

export const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState,
    reducers: {
        resetPagination: (state: DashboardState) => {
            state.pagination = {
                currentPage: 1,
                pageSize: 20,
                total: 0,
            };
        },
    },
    extraReducers: (builder) => {
        // Visit request data
        builder
            .addCase(dashboardVisitRequest.fulfilled, (
                state:DashboardState,
                action:PayloadAction<DataProps>,
            ) => {
                state.isFetching = false;
                if (action.payload.visitRequests) {
                    state.requestData = action.payload.visitRequests.data;
                    state.pagination = {
                        currentPage: action.payload.visitRequests.meta.current_page || 1,
                        pageSize: action.payload.visitRequests.meta.per_page || 20,
                        total: action.payload.visitRequests.meta.total || 0,
                    };
                } else {
                    state.requestData = [emptyRequestState];
                }

                if (action.payload.summary) {
                    state.summaryData = action.payload.summary;
                }
                if (action.payload.filters) {
                    state.filterData = action.payload.filters;
                }
            });

        builder
            .addCase(dashboardFilterRequest.fulfilled, (
                state:DashboardState,
                action:PayloadAction<DataProps>,
            ) => {
                state.isFetching = false;
                if (action.payload.visitRequests) {
                    state.requestData = action.payload.visitRequests.data;
                    state.pagination = {
                        currentPage: action.payload.visitRequests.meta.current_page || 1,
                        pageSize: action.payload.visitRequests.meta.per_page || 20,
                        total: action.payload.visitRequests.meta.total || 0,
                    };
                } if (action.payload.summary) {
                    state.summaryData = action.payload.summary;
                } if (action.payload.filters) {
                    state.filterData = action.payload.filters;
                } else if (!action.payload.visitRequests) {
                    // A list-only page (paging) carries rows but no summary or
                    // filters, and keeps the ones already loaded. Only a reply
                    // with neither clears the table.
                    state.requestData = [emptyRequestState];
                }
            });

        // The export runs on its own flag: it polls for up to two minutes, and
        // driving isFetching would hold the request table in a loading state for
        // all of it. It also keeps running if the user closes the export modal.
        // Counted, not flagged, so one export finishing cannot clear the progress
        // state while another is still going.
        builder
            .addCase(exportCompletedVisits.pending, (state:DashboardState) => {
                state.activeExportCount += 1;
                state.isExporting = true;
                state.error = null;
            })
            .addCase(exportCompletedVisits.fulfilled, (state:DashboardState) => {
                state.activeExportCount = Math.max(0, state.activeExportCount - 1);
                state.isExporting = state.activeExportCount > 0;
            })
            .addCase(exportCompletedVisits.rejected, (state:DashboardState, action) => {
                state.activeExportCount = Math.max(0, state.activeExportCount - 1);
                state.isExporting = state.activeExportCount > 0;
                state.error = action.error;
            });

        // Pending state for all requests
        builder.addMatcher(
            isAnyOf(
                dashboardVisitRequest.pending,
                dashboardFilterRequest.pending,
            ),
            (state:DashboardState) => {
                state.isFetching = true;
                state.error = null;
            },
        );

        // Error state for all requests
        builder.addMatcher(
            isAnyOf(
                dashboardVisitRequest.rejected,
                dashboardFilterRequest.rejected,
            ),
            (state:DashboardState, action) => {
                state.isFetching = false;
                state.error = action.error;
            },
        );
    },
});

// This slice is persisted, but the export progress state is driven by a poll
// that dies with the page: a stored "exporting" flag would come back permanently
// stuck, showing the indicator and disabling the Export button with nothing
// running. Strip it on the way in and out of storage. Registered in store.ts.
export const dashboardExportTransform = createTransform<DashboardState, DashboardState>(
    (inboundState) => ({ ...inboundState, isExporting: false, activeExportCount: 0 }),
    (outboundState) => ({ ...outboundState, isExporting: false, activeExportCount: 0 }),
    { whitelist: ['dashboard'] },
);

// this is for configureStore
export default dashboardSlice.reducer;
export const { resetPagination } = dashboardSlice.actions;
